<template>
    <div v-if="canReadProduct" class="scrollable-container" ref="containerRef">
        <div class="scrollable-content" ref="contentRef">
            <AppLoader v-if="loading" />
            <div class="food-cards-container">
                <div v-for="item in filteredFoodItems" :key="item.id" class="food-card" @click="openFoodModal(item)">
                    <img :src="item.image_url ? proxy.$customImageUtil.getImageUrl(item.image_url) : DEFAULT_PRODUCT_IMAGE"
                        width="100%" height="100%" :alt="item.name" />
                    <h3>{{ item.name }}</h3>
                    <div class="food-price">
                        <IconCurrencyBaht :width="12" :height="18" :stroke-width="2" />
                        <span>{{ determineProductBasePrice(item).toFixed(2) }}</span>
                    </div>
                </div>
            </div>
            <CustomScroller ref="customScroller" />
        </div>
    </div>
    <!-- Modals -->
    <CheckoutModal :is-open="openCheckoutModal" @update:isOpen="openCheckoutModal = $event" />
</template>

<script setup>
import { defineEmits, defineProps, computed, getCurrentInstance, onMounted, onBeforeUnmount, ref } from 'vue';
import eventBus from '@/eventBus';
import { useDeviceVerifierStore } from '@/stores/deviceVerifier';
import { usePanelScroller } from '@/composables/usePanelScroller';
import { useCheckoutState } from '@/composables/useCheckoutState';
import { useOrderGuard } from '@/composables/useOrderGuard';
import { useProduct } from "@/composables/useProduct";
import AppLoader from '@/components/base/appLoader.vue';
import IconCurrencyBaht from '@/components/icons/iconCurrencyBaht.vue';
import CustomScroller from '@/components/order/customScroller.vue';
import CheckoutModal from '@/components/order/modals/checkoutModal.vue';
import { DEFAULT_PRODUCT_IMAGE } from '@/constants/image';

const props = defineProps({
    category: {
        type: String,
        required: true
    },
    searchQuery: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['open-food-modal']);

const { proxy } = getCurrentInstance();
const { containerRef, contentRef, customScroller, initializeWithRetry } = usePanelScroller();

const { getIsVerified } = useDeviceVerifierStore();
const { canReadProduct, fetchProducts, determineProductBasePrice } = useProduct();

const foodItems = ref([]);
const loading = ref(false);

const { openCheckoutModal, setOpenCheckoutModal } = useCheckoutState();
// Guard for order modification
const { canModifyOrder } = useOrderGuard(() => { setOpenCheckoutModal(true); });

const filteredFoodItems = computed(() => {
    return foodItems.value.filter((item) => {
        const matchesCategory = item.category.name.toLowerCase() === props.category.toLowerCase();
        const matchesSearch = props.searchQuery ?
            item.name.toLowerCase().includes(props.searchQuery.toLowerCase()) :
            true;
        return matchesCategory && matchesSearch;
    });
});

const openFoodModal = (item) => {
    if (!canModifyOrder()) return;
    emit('open-food-modal', item)
}

onMounted(async () => {
    if(getIsVerified()) {
        try {
            loading.value = true;
            foodItems.value = await fetchProducts();
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            loading.value = false;
        }
    }
    // Listen for categoryChanged event and trigger scroller re-init
    eventBus.on('categoryChanged', initializeWithRetry);
});

onBeforeUnmount(() => {
    eventBus.off('categoryChanged', initializeWithRetry);
});
</script>